import { memo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ScrollText, Download, Search, Bot, User, Settings,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { WorkflowAuditEntry } from '@/types/api';

const ENTITY_TYPES = [
  { value: 'all', label: 'All Entities' },
  { value: 'project', label: 'Projects' },
  { value: 'task', label: 'Tasks' },
  { value: 'checklist_item', label: 'Checklists' },
  { value: 'attestation', label: 'Attestations' },
  { value: 'escalation', label: 'Escalations' },
  { value: 'assignment', label: 'Assignments' },
] as const;

const ENTITY_COLORS: Record<string, string> = {
  project: 'bg-teal/15 text-teal-light border-teal/30',
  task: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  checklist_item: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  attestation: 'bg-green-500/15 text-green-400 border-green-500/30',
  escalation: 'bg-red-500/15 text-red-400 border-red-500/30',
  assignment: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
};

const ACTOR_ICONS: Record<string, typeof User> = {
  user: User,
  agent: Bot,
  system: Settings,
};

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatAction(action: string): string {
  return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function AuditTrailPage() {
  const user = useAuthStore((s) => s.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const entityFilter = searchParams.get('entity') ?? 'all';
  const searchQuery = searchParams.get('q') ?? '';
  const limit = parseInt(searchParams.get('limit') ?? '100', 10);

  const setEntityFilter = useCallback((v: string) => {
    setSearchParams((prev) => { prev.set('entity', v); return prev; });
  }, [setSearchParams]);

  const setLimit = useCallback((v: number) => {
    setSearchParams((prev) => { prev.set('limit', String(v)); return prev; });
  }, [setSearchParams]);

  const setSearch = useCallback((v: string) => {
    setSearchParams((prev) => { if (v) prev.set('q', v); else prev.delete('q'); return prev; });
  }, [setSearchParams]);

  const { data, isLoading } = useQuery({
    queryKey: ['audit-trail', limit],
    queryFn: () => api.dashboard.auditLog({ limit }),
    enabled: !!user,
    staleTime: 30_000,
  });

  const entries = data?.entries ?? [];

  const filtered = entries.filter((entry) => {
    if (entityFilter !== 'all' && entry.entityType !== entityFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return entry.action.toLowerCase().includes(q) || entry.entityType.toLowerCase().includes(q) || entry.entityId.toLowerCase().includes(q) || (entry.actorId?.toLowerCase().includes(q) ?? false);
    }
    return true;
  });

  const handleExportPdf = useCallback(() => {
    window.open(api.reports.auditLogPdf(limit), '_blank');
  }, [limit]);

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <PageHeader
        title="Audit Trail"
        subtitle="Immutable record of all compliance workflow actions"
        actions={
          <Button variant="outline" size="sm" onClick={handleExportPdf} aria-label="Export audit trail as PDF">
            <Download className="mr-1.5 h-3.5 w-3.5" />Export PDF
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span className="text-muted-foreground"><span className="font-semibold text-foreground">{entries.length}</span> entries loaded</span>
        <span className="text-muted-foreground"><span className="font-semibold text-foreground">{filtered.length}</span> matching</span>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={entityFilter} onValueChange={(v) => setEntityFilter(v ?? 'all')}>
          <SelectTrigger size="sm" className="w-44"><SelectValue placeholder="All Entities" /></SelectTrigger>
          <SelectContent>
            {ENTITY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={String(limit)} onValueChange={(v) => setLimit(parseInt(v ?? '100', 10))}>
          <SelectTrigger size="sm" className="w-28"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="50">50 rows</SelectItem>
            <SelectItem value="100">100 rows</SelectItem>
            <SelectItem value="200">200 rows</SelectItem>
          </SelectContent>
        </Select>

        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search actions, entities, actors..." value={searchQuery} onChange={(e) => setSearch(e.target.value)} className="h-7 pl-8 text-sm" />
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-border">
          <div className="flex items-center gap-3 border-b border-border bg-muted/30 px-4 py-2">
            <div className="w-36 shrink-0 h-3 animate-pulse rounded bg-muted" />
            <div className="w-24 shrink-0 h-3 animate-pulse rounded bg-muted" />
            <div className="flex-1 h-3 animate-pulse rounded bg-muted" />
          </div>
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="flex items-center gap-3 border-b border-border px-4 py-3">
              <div className="w-36 shrink-0 h-3 animate-pulse rounded bg-muted" />
              <div className="w-24 shrink-0 h-5 animate-pulse rounded bg-muted" />
              <div className="flex-1 h-3.5 animate-pulse rounded bg-muted" />
              <div className="w-20 shrink-0 h-3 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title="No audit entries found"
          description={searchQuery || entityFilter !== 'all' ? 'Try adjusting your filters or search query.' : 'Audit entries will appear here as actions occur.'}
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block rounded-lg border border-border">
            <div className="flex items-center gap-3 border-b border-border bg-muted/30 px-4 py-2">
              <div className="w-36 shrink-0 text-xs font-medium uppercase tracking-wider text-muted-foreground">Timestamp</div>
              <div className="w-24 shrink-0 text-xs font-medium uppercase tracking-wider text-muted-foreground">Entity</div>
              <div className="flex-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">Action</div>
              <div className="w-20 shrink-0 text-xs font-medium uppercase tracking-wider text-muted-foreground">Actor</div>
              <div className="w-40 shrink-0 text-xs font-medium uppercase tracking-wider text-muted-foreground">Details</div>
            </div>
            {filtered.map((entry) => <AuditRow key={entry.entryId} entry={entry} />)}
          </div>

          {/* Mobile card layout */}
          <div className="sm:hidden space-y-2">
            {filtered.map((entry) => <AuditCardMobile key={entry.entryId} entry={entry} />)}
          </div>
        </>
      )}
    </div>
  );
}

const AuditRow = memo(function AuditRow({ entry }: { readonly entry: WorkflowAuditEntry }) {
  const [expanded, setExpanded] = useState(false);
  const ActorIcon = ACTOR_ICONS[entry.actorType] ?? User;
  const entityColor = ENTITY_COLORS[entry.entityType] ?? 'bg-muted text-muted-foreground';

  return (
    <div className="border-b border-border last:border-0">
      <button
        type="button"
        className="flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/30"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-label={`${formatAction(entry.action)} on ${entry.entityType}`}
      >
        <div className="w-36 shrink-0 text-xs text-muted-foreground tabular-nums">{formatTimestamp(entry.timestamp)}</div>
        <div className="w-24 shrink-0">
          <Badge variant="outline" className={cn('text-[10px] capitalize', entityColor)}>{entry.entityType.replace('_', ' ')}</Badge>
        </div>
        <div className="flex-1 text-sm font-medium text-foreground">{formatAction(entry.action)}</div>
        <div className="flex w-20 shrink-0 items-center gap-1">
          <ActorIcon className={cn('h-3 w-3', entry.actorType === 'agent' ? 'text-purple-400' : 'text-muted-foreground')} />
          <span className="text-xs capitalize text-muted-foreground">{entry.actorType}</span>
        </div>
        <div className="w-40 shrink-0 truncate text-xs text-muted-foreground">{entry.entityId.slice(0, 8)}...</div>
      </button>

      {expanded && (
        <div className="border-t border-border/50 bg-muted/10 px-4 py-3">
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div><span className="font-medium text-muted-foreground">Entity ID:</span><span className="ml-2 font-mono text-foreground">{entry.entityId}</span></div>
            <div><span className="font-medium text-muted-foreground">Actor ID:</span><span className="ml-2 font-mono text-foreground">{entry.actorId ?? '—'}</span></div>
          </div>
          {entry.oldValue && (
            <div className="mt-3">
              <span className="text-xs font-medium text-muted-foreground">Previous State:</span>
              <pre className="mt-1 max-h-32 overflow-auto rounded bg-muted/50 p-2 text-[11px] text-foreground">{JSON.stringify(entry.oldValue, null, 2)}</pre>
            </div>
          )}
          {entry.newValue && (
            <div className="mt-2">
              <span className="text-xs font-medium text-muted-foreground">New State:</span>
              <pre className="mt-1 max-h-32 overflow-auto rounded bg-muted/50 p-2 text-[11px] text-foreground">{JSON.stringify(entry.newValue, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

function AuditCardMobile({ entry }: { readonly entry: WorkflowAuditEntry }) {
  const [expanded, setExpanded] = useState(false);
  const entityColor = ENTITY_COLORS[entry.entityType] ?? 'bg-muted text-muted-foreground';

  return (
    <button type="button" className="w-full text-left rounded-lg border bg-card p-3" onClick={() => setExpanded(!expanded)} aria-expanded={expanded}>
      <div className="flex items-center justify-between">
        <Badge variant="outline" className={cn('text-[10px] capitalize', entityColor)}>{entry.entityType.replace('_', ' ')}</Badge>
        <span className="text-[10px] text-muted-foreground tabular-nums">{formatTimestamp(entry.timestamp)}</span>
      </div>
      <p className="mt-1 text-sm font-medium">{formatAction(entry.action)}</p>
      <p className="text-[10px] text-muted-foreground capitalize">{entry.actorType} · {entry.entityId.slice(0, 8)}</p>
      {expanded && entry.newValue && (
        <pre className="mt-2 max-h-24 overflow-auto rounded bg-muted/50 p-2 text-[10px]">{JSON.stringify(entry.newValue, null, 2)}</pre>
      )}
    </button>
  );
}
